import { memo } from 'react';
import ReactMarkdown, { type Options, type Components } from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';

const remarkPlugins: NonNullable<Options['remarkPlugins']> = [remarkGfm];
const rehypePlugins: NonNullable<Options['rehypePlugins']> = [[rehypeHighlight, { detect: false }]];

/** Shared counter so each copy button gets a unique ID. */
let copyCounter = 0;

const components: Components = {
  table: ({ children, ...props }) => (
    <div className="md-table-wrap">
      <table {...props}>{children}</table>
    </div>
  ),
  code: ({ children, ...props }) => (
    <code className="md-code" {...props}>{children}</code>
  ),
  pre: ({ children, ...props }) => {
    const match = /language-(\w+)/.exec((props.className ?? '') as string);
    const lang = match ? match[1] : '';
    const copyId = `copy-md-${++copyCounter}`;
    const text = typeof children === 'string' ? children : String(children);
    return (
      <div className="md-code-block">
        {lang ? (
          <div className="md-code-header">
            <span className="md-code-lang">{lang}</span>
            <button
              type="button"
              className="copy-btn"
              aria-label="Copy"
              title="Copy"
              onClick={() => {
                void navigator.clipboard?.writeText(text).then(() => {
                  const btn = document.getElementById(copyId);
                  if (btn) {
                    btn.textContent = 'Copied';
                    setTimeout(() => { btn.textContent = 'Copy'; }, 1500);
                  }
                });
              }}
              id={copyId}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            </button>
          </div>
        ) : null}
        <pre {...props}>{children}</pre>
      </div>
    );
  },
  blockquote: ({ children, ...props }) => (
    <blockquote className="md-blockquote" {...props}>{children}</blockquote>
  ),
  a: ({ children, ...props }) => (
    <a className="md-link" {...props}>{children}</a>
  ),
  ul: ({ children, ...props }) => (
    <ul className="md-list" {...props}>{children}</ul>
  ),
  ol: ({ children, ...props }) => (
    <ol className="md-list" {...props}>{children}</ol>
  ),
  li: ({ children, ...props }) => (
    <li className="md-list-item" {...props}>{children}</li>
  ),
};

export const Markdown = memo(function Markdown({ text }: { text: string }) {
  return (
    <div className="md">
      <ReactMarkdown remarkPlugins={remarkPlugins} rehypePlugins={rehypePlugins} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  );
});
